import {
  Alert,
  AlertColor,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Tooltip,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { Fragment, useEffect, useRef, useState } from "react";
import { removeRequestToSend } from "../../../redux/features/appStateSlice";
import { codes } from "../../../constante/code";
import { RequestToSend } from "../../../models/RequestToSend";
import { planoAPI } from "../../../redux/features/cache";
import { TagDescription } from "@reduxjs/toolkit/query";

interface RequestsStatus {
  status: string;
  statusText: string;
  tooltip: string;
  id: string;
}

export default function MutationHandler(props: { children: any }) {
  const gridBorder = "1px solid #c7c7c7";
  const [showModalQuestions, setShowModalQuestions] = useState<boolean>(false);
  const [dialogText, setDialogText] = useState<string>("");
  const resolver = useRef<undefined | ((value: string) => void)>(undefined);
  const requestsInQueue = useAppSelector(
    (state) => state.appState.requestsInQueue
  );
  const [requestsDone, setRequestsDone] = useState<RequestsStatus[]>([]);
  const requestsDoneRef = useRef<RequestsStatus[]>([]);
  const dispatch = useAppDispatch();
  const question = useRef<string>("");
  const isFetching = useRef<boolean>(false);
  const requestsToInvalidate = useRef<string[]>([]);

  const addRequestStatus = (newObject: RequestsStatus) => {
    const newArray = [...requestsDoneRef.current, newObject];
    setRequestsDone(newArray);
    requestsDoneRef.current = newArray;
  };

  const addRequestToInvalidate = (requests: string[]) => {
    requests.forEach((r) => {
      if (!requestsToInvalidate.current.includes(r))
        requestsToInvalidate.current.push(r);
    });
  };

  const fetchRequests = async (requests: RequestToSend[]) => {
    for (let request of requests) {
      isFetching.current = true;
      let result: any;
      if (!requestsDoneRef.current.find((r) => r.id === request.id)) {
        try {
          result = await request
            .cacheMethod({
              ...request.parameters,
              body: request.body,
              question: question.current,
            })
            .unwrap();
          addRequestStatus({
            id: request.id,
            status: "success",
            statusText: "Succès",
            tooltip: "",
          });
          request.successAction(result as any);
          addRequestToInvalidate(request.requestsToInvalidate);
        } catch (error) {
          if (
            (error as any).status === 422 &&
            codes.includes((error as any).data.code)
          ) {
            result = error;
            let answer = "";
            while (
              (result as any)?.status === 422 &&
              codes.includes((result as any).data.code)
            ) {
              setShowModalQuestions(true);
              let errorMessage = (result as any).data.detail.startsWith(
                "Param <"
              )
                ? (result as any).data.detail.substring(
                    (result as any).data.detail.indexOf(":") + 1
                  )
                : (result as any).data.detail;
              setDialogText(errorMessage);
              const promise = new Promise(
                (resolve: (value: string) => void, reject) => {
                  resolver.current = resolve;
                }
              );
              answer = await promise;
              question.current =
                question.current + `${(result as any).data.code}=${answer}&`;
              try {
                result = await request
                  .cacheMethod({
                    ...request.parameters,
                    body: request.body,
                    question: question.current,
                  })
                  .unwrap();
              } catch (error) {
                result = error;
              }
            }
            addRequestStatus({
              id: request.id,
              status: (result as any)?.status ? "error" : "success",
              statusText: (result as any)?.status ? "Erreur" : "Succès",
              tooltip: (result as any)?.status
                ? (result as any)?.data?.detail
                : "",
            });
            request.successAction(result as any);
            addRequestToInvalidate(request.requestsToInvalidate);
          } else {
            addRequestStatus({
              id: request.id,
              status: "error",
              statusText: "Erreur",
              tooltip: (error as any).data.detail,
            });
          }
        }
      }
      isFetching.current = false;
    }
  };

  useEffect(() => {
    const fetch = async () => {
      if (isFetching.current) {
        setTimeout(() => {
          fetch();
        }, 500);
      } else {
        await fetchRequests(requestsInQueue);
      }
    };
    if (requestsInQueue.length === 0) question.current = "";
    else {
      fetch();
    }
  }, [requestsInQueue.length]);

  return (
    <Box>
      <Dialog
        open={requestsInQueue.length > 0}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{ zIndex: 2147483647 }}
        PaperProps={{ sx: { maxWidth: "50vw" } }}
      >
        <DialogTitle id="alert-dialog-title">{`${requestsInQueue.length} Opération(s)`}</DialogTitle>
        <Grid
          container
          spacing={2}
          maxWidth={"100%"}
          alignSelf={"center"}
          flexDirection={"column"}
          p={2}
          m={0}
        >
          {requestsInQueue.map((r, i) => {
            const statut = requestsDone.find((request) => request.id === r.id);
            return (
              <Box
                key={r.title}
                display={"flex"}
                justifyContent={"space-between"}
              >
                <Grid
                  item
                  xs
                  display={"flex"}
                  alignItems={"center"}
                  borderTop={gridBorder}
                  borderLeft={gridBorder}
                  borderBottom={
                    i === requestsInQueue.length - 1 ? gridBorder : ""
                  }
                  p={1}
                >
                  <DialogContentText
                    display={"flex"}
                    alignItems={"center"}
                    fontWeight={"bold"}
                    whiteSpace={"nowrap"}
                    p={1}
                  >
                    {r.title}
                  </DialogContentText>
                </Grid>
                <Grid
                  item
                  borderTop={gridBorder}
                  borderRight={gridBorder}
                  borderBottom={
                    i === requestsInQueue.length - 1 ? gridBorder : ""
                  }
                  p={1}
                  display={"flex"}
                  justifyContent={"center"}
                >
                  <Tooltip title={statut?.tooltip}>
                    <Alert
                      severity={
                        statut?.status ? (statut.status as AlertColor) : "info"
                      }
                      sx={{ width: "fit-content", maxWidth: "250px" }}
                      variant="filled"
                    >
                      {statut?.tooltip
                        ? statut?.tooltip
                        : statut?.statusText
                        ? statut?.statusText
                        : "En cours"}
                    </Alert>
                  </Tooltip>
                </Grid>
              </Box>
            );
          })}
        </Grid>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setRequestsDone([]);
              requestsDoneRef.current = [];
              dispatch(removeRequestToSend());
              dispatch(
                planoAPI.util.invalidateTags(
                  requestsToInvalidate.current as TagDescription<any>
                )
              );
              requestsToInvalidate.current = [];
            }}
            disabled={requestsInQueue.length !== requestsDone.length}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showModalQuestions}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{ zIndex: 2147483647 }}
      >
        <DialogTitle id="alert-dialog-title">{"Important"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resolver.current!("true");
              setShowModalQuestions(false);
            }}
          >
            Confirmer
          </Button>
          <Button
            onClick={() => {
              resolver.current!("false");
              setShowModalQuestions(false);
            }}
            autoFocus
          >
            Refuser
          </Button>
        </DialogActions>
      </Dialog>
      {props.children}
    </Box>
  );
}
